import { useCallback, useEffect, useRef, useState } from 'react'
import { useV2Api } from '../../../hooks/useV2Api'

/**
 * SmartAddressInput — single field that accepts city, port, address, or zipcode
 * (Flexport-style). Mirrors the screenshots: dropdown groups results by
 * Airport / Ocean Port / City. On select, emits a normalised payload:
 *   { type: 'port',    locode, displayName, city, countryCode }
 *   { type: 'address', displayName, ... parsed components }
 *
 * Pulls port suggestions from /api/v2/ports and address suggestions from
 * /api/v2/places/autocomplete (Google Places via backend proxy).
 */
export default function SmartAddressInput({
  value,
  onChange,
  placeholder = 'City, port, address or zip code',
  disabled = false,
  side = 'origin', // 'origin' | 'destination' — informational only
  mode = 'any' // 'sea' | 'air' | 'any' — gates B4 inland-fallback hint
}) {
  const api = useV2Api()
  const [inputValue, setInputValue] = useState(
    typeof value === 'object' && value ? (value.displayName || '') : (value || '')
  )
  const [groups, setGroups] = useState({ airports: [], ports: [], cities: [] })
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  // B2 PORT-04 — surfaced from /api/v2/ports `meta.didYouMean` when the
  // user's typo only matches via trigram fallback (e.g. "Shangai" → Shanghai).
  const [didYouMean, setDidYouMean] = useState(null)
  // B4 PORT-06 — surfaced from /api/v2/ports `meta.nearestSeaport` when
  // the user types an inland city in ocean mode and no real seaport
  // matches.
  const [nearestSeaport, setNearestSeaport] = useState(null)
  const debounceRef = useRef(null)
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (typeof value === 'object' && value) {
      setInputValue(value.displayName || '')
    } else if (typeof value === 'string') {
      setInputValue(value)
    } else if (value == null) {
      setInputValue('')
    }
  }, [value])

  useEffect(() => {
    const close = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setGroups({ airports: [], ports: [], cities: [] })
      setShowDropdown(false)
      setDidYouMean(null)
      setNearestSeaport(null)
      return
    }
    setLoading(true)
    try {
      // In ocean mode, ask the BE to also surface the nearest seaport
      // when no port matches (B4 — PORT-06). The BE gate ensures we only
      // pay for Google Geocoding when there are zero port hits.
      const portsType = mode === 'sea' || mode === 'air' ? mode : undefined
      const portsOpts = mode === 'sea' ? { suggestNearest: true } : {}
      const [portsRes, placesRes] = await Promise.all([
        api.searchPorts(q, portsType, portsOpts).catch(() => ({ data: [] })),
        api.request(`/api/v2/places/autocomplete?${new URLSearchParams({ input: q, types: 'geocode' })}`)
          .catch(() => ({ data: [] }))
      ])
      const portRows = portsRes?.data || []
      const placeRows = placesRes?.data || []

      setGroups({
        airports: portRows.filter(p => p.port_type === 'air'),
        ports: portRows.filter(p => p.port_type === 'sea'),
        cities: placeRows
      })
      setDidYouMean(portsRes?.meta?.didYouMean || null)
      setNearestSeaport(portsRes?.meta?.nearestSeaport || null)
      const hasAny =
        portRows.length > 0 ||
        placeRows.length > 0 ||
        !!portsRes?.meta?.didYouMean ||
        !!portsRes?.meta?.nearestSeaport
      setShowDropdown(hasAny)
    } catch {
      setGroups({ airports: [], ports: [], cities: [] })
      setDidYouMean(null)
      setNearestSeaport(null)
      setShowDropdown(false)
    } finally {
      setLoading(false)
    }
  }, [api, mode])

  const handleInputChange = (e) => {
    const v = e.target.value
    setInputValue(v)
    onChange(null) // clear structured value when user types
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250)
  }

  const handleSelectPort = (p) => {
    const payload = {
      type: 'port',
      locode: p.locode,
      portType: p.port_type,
      displayName: p.display_name,
      city: p.city,
      countryCode: p.country_code,
      countryName: p.country_name
    }
    setInputValue(p.display_name)
    onChange(payload)
    setShowDropdown(false)
  }

  const handleSelectPlace = async (place) => {
    // Place suggestions from /api/v2/places/autocomplete have shape:
    // { placeId, description, mainText, secondaryText }. We commit the basic
    // payload immediately so the form is never visually stuck, then fetch
    // parsed address components from the backend Place Details proxy and
    // patch the structured fields in.
    const basePayload = {
      type: 'address',
      displayName: place.description,
      street: place.mainText,
      placeId: place.placeId,
      secondaryText: place.secondaryText
    }
    setInputValue(place.description)
    onChange(basePayload)
    setShowDropdown(false)

    try {
      const detailsRes = await api.placeDetails(place.placeId)
      const data = detailsRes?.data || detailsRes
      const parsed = data?.parsed
      if (parsed) {
        onChange({
          ...basePayload,
          displayName: data.formattedAddress || place.description,
          street: parsed.street || place.mainText || null,
          city: parsed.city || null,
          state: parsed.state || null,
          stateCode: parsed.stateCode || null,
          zip: parsed.zip || null,
          country: parsed.country || null,
          countryCode: parsed.countryCode || null,
          latitude: data.lat || null,
          longitude: data.lng || null
        })
      }
    } catch {
      // Place Details failed — keep the basic payload. The form still
      // submits with the raw description string; backend accepts that shape.
    }
  }

  const groupHeader = (label) => (
    <li style={{ padding: '6px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8F432F', background: '#F3F2ED' }}>
      {label}
    </li>
  )

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onFocus={() => {
          const hasAny = groups.airports.length || groups.ports.length || groups.cities.length
          if (hasAny) setShowDropdown(true)
        }}
        autoComplete="off"
        aria-label={`${side} address`}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #DDDCD1', borderRadius: 6, fontSize: 14 }}
      />
      {loading && (
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#8F8F85' }}>
          …
        </span>
      )}
      {showDropdown && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#FFFFFF', border: '1px solid #DDDCD1', borderRadius: 6,
          maxHeight: 320, overflow: 'auto', margin: '4px 0 0', padding: 0,
          listStyle: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          {nearestSeaport && (
            <li
              data-testid="port-nearest-seaport"
              onClick={() => handleSelectPort({
                locode: nearestSeaport.locode,
                port_type: nearestSeaport.port_type || 'sea',
                display_name: nearestSeaport.display_name,
                city: nearestSeaport.city || null,
                country_code: nearestSeaport.country_code || null,
                country_name: nearestSeaport.country_name || null
              })}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F2ED' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#FFF8E7' }}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                background: '#FFF8E7', borderBottom: '1px solid #F0EEE7',
                color: '#23201C'
              }}
            >
              No seaport for <strong>{inputValue}</strong>{' '}— nearest is{' '}
              <strong>{nearestSeaport.display_name}</strong>{' '}
              <span style={{ color: '#8F8F85', fontSize: 12 }}>
                ({nearestSeaport.locode}
                {typeof nearestSeaport.distanceKm === 'number'
                  ? `, ~${nearestSeaport.distanceKm.toLocaleString()} km`
                  : ''})
              </span>
              <button
                type="button"
                style={{
                  marginLeft: 8, padding: '2px 8px', fontSize: 12,
                  background: '#23201C', color: '#FFFFFF', border: 'none',
                  borderRadius: 4, cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSelectPort({
                    locode: nearestSeaport.locode,
                    port_type: nearestSeaport.port_type || 'sea',
                    display_name: nearestSeaport.display_name,
                    city: nearestSeaport.city || null,
                    country_code: nearestSeaport.country_code || null,
                    country_name: nearestSeaport.country_name || null
                  })
                }}
              >
                Use this port
              </button>
            </li>
          )}
          {didYouMean && (
            <li
              data-testid="port-didyoumean"
              onClick={() => handleSelectPort({
                locode: didYouMean.locode,
                port_type: didYouMean.port_type,
                display_name: didYouMean.display_name,
                city: didYouMean.city || null,
                country_code: didYouMean.country_code || null,
                country_name: didYouMean.country_name || null
              })}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F2ED' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#FFF8E7' }}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                background: '#FFF8E7', borderBottom: '1px solid #F0EEE7',
                color: '#23201C'
              }}
            >
              Did you mean <strong>{didYouMean.display_name}</strong>{' '}
              <span style={{ color: '#8F8F85', fontSize: 12 }}>({didYouMean.locode})</span>?
            </li>
          )}
          {groups.airports.length > 0 && (
            <>
              {groupHeader('✈ Airport')}
              {groups.airports.map(p => (
                <li key={`a-${p.locode}`} onClick={() => handleSelectPort(p)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F2ED' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #F0EEE7' }}>
                  <strong>{p.display_name}</strong>{' '}
                  <span style={{ color: '#8F8F85', fontSize: 12 }}>({p.locode})</span>
                </li>
              ))}
            </>
          )}
          {groups.ports.length > 0 && (
            <>
              {groupHeader('⚓ Ocean Port')}
              {groups.ports.map(p => (
                <li key={`p-${p.locode}`} onClick={() => handleSelectPort(p)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F2ED' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #F0EEE7' }}>
                  <strong>{p.display_name}</strong>{' '}
                  <span style={{ color: '#8F8F85', fontSize: 12 }}>({p.locode})</span>
                </li>
              ))}
            </>
          )}
          {groups.cities.length > 0 && (
            <>
              {groupHeader('📍 City / Address')}
              {groups.cities.map(s => (
                <li key={`c-${s.placeId}`} onClick={() => handleSelectPlace(s)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F2ED' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #F0EEE7' }}>
                  <strong>{s.mainText}</strong>
                  {s.secondaryText && (
                    <span style={{ marginLeft: 6, fontSize: 12, color: '#8F8F85' }}>{s.secondaryText}</span>
                  )}
                </li>
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  )
}
